/**
 * Membership launch toggle.
 *
 * Single source of truth for whether the membership surface
 * (/membership, /register-membership, /billing) is public or still
 * hidden behind the admin-only pre-launch gate.
 *
 * Set at build time via a Vite env var so both the browser bundle and
 * the server runtime resolve the same value:
 *
 *   VITE_MEMBERSHIP_PUBLIC=true    → public (launch mode)
 *   VITE_MEMBERSHIP_PUBLIC unset   → admin-only (default, pre-launch)
 *
 * When it flips to true:
 *   • The launch gate stops locking the three membership routes.
 *   • /membership and /register-membership drop their admin guard and
 *     render the member-facing purchase entry point.
 *   • createFoundingCheckoutSession stops requiring admin (any signed-in
 *     user can start their own checkout; target_email overrides still
 *     require admin).
 *   • robots.txt drops its Disallow lines for the three routes.
 *   • sitemap.xml adds /membership and /register-membership.
 *
 * Flip the flag in your deployment env when you're ready — no code
 * changes required.
 */

// `import.meta.env.VITE_*` is inlined at build time on both the browser
// and worker builds, so the same value applies in every runtime.
const RAW =
  (typeof import.meta !== "undefined" &&
    (import.meta as unknown as { env?: Record<string, string | undefined> })
      .env?.VITE_MEMBERSHIP_PUBLIC) ??
  // Fallback for pure-Node contexts (tests, scripts) where import.meta.env
  // may not be populated.
  (typeof process !== "undefined"
    ? process.env?.VITE_MEMBERSHIP_PUBLIC ?? process.env?.MEMBERSHIP_PUBLIC
    : undefined);

/**
 * Membership checkout is now open to any verified signed-in user in the
 * run-up to launch (Jana, 13 July 2026). The Founding rate is still
 * time-limited by copy on the site; this flag just controls whether the
 * server function refuses non-admin callers.
 */
export function isMembershipPublic(): boolean {
  if (!RAW) return true;
  const v = String(RAW).trim().toLowerCase();
  if (v === "false" || v === "0" || v === "no" || v === "off") return false;
  return true;
}

/** Paths controlled by this toggle — shared between launch-gate, robots, sitemap. */
export const MEMBERSHIP_PATHS = [
  "/membership",
  "/register-membership",
  "/billing",
] as const;
